import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Package, Tag, Cog, RulerDimensionLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import EquipmentCatalogTab from './equipment/EquipmentCatalogTab';
import BrandsTab from './equipment/BrandsTab';
import EquipmentTypesTab from './equipment/EquipmentTypesTab';
import SpecificationsTab from './equipment/SpecificationsTab';

const Equipment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get active tab from URL or default to 'catalog'
  const getActiveTabFromUrl = () => {
    const path = location.pathname;
    if (path.includes('/brands')) return 'brands';
    if (path.includes('/types')) return 'types';
    if (path.includes('/specs')) return 'specs';
    return 'catalog';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());

  const tabs = [
    {
      id: 'catalog',
      name: 'Equipos',
      icon: Package,
      color: 'purple',
      component: EquipmentCatalogTab
    },
    {
      id: 'brands',
      name: 'Marcas',
      icon: Tag,
      color: 'blue',
      component: BrandsTab
    },
    {
      id: 'types',
      name: 'Tipos de Equipo',
      icon: Cog,
      color: 'green',
      component: EquipmentTypesTab
    },
    {
      id: 'specs',
      name: 'Especificaciones',
      icon: RulerDimensionLine,
      color: 'yellow',
      component: SpecificationsTab
    }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Update URL without page reload
    const basePath = '/equipment';
    const newPath = tabId === 'catalog' ? basePath : `${basePath}/${tabId}`;
    navigate(newPath, { replace: true });
  };

  const getTabColorClasses = (tab, isActive) => {
    const colorMap = {
      purple: {
        active: 'border-purple-500 dark:border-purple-400 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
        inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-600'
      },
      blue: {
        active: 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
        inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-600'
      },
      green: {
        active: 'border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
        inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:border-green-300 dark:hover:border-green-600'
      },
      yellow: {
        active: 'border-yellow-500 dark:border-yellow-400 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
        inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:border-yellow-300 dark:hover:border-yellow-600'
      }
    };

    return colorMap[tab.color][isActive ? 'active' : 'inactive'];
  };

  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Breadcrumb */}
        <div className="mb-4 sm:mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm sm:text-base">Volver al Dashboard</span>
          </Link>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Header */}
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Catálogo de equipos</h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Administra catálogo, marcas, tipos y especificaciones</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex px-4 sm:px-6 overflow-x-auto scrollbar-hide" aria-label="Tabs">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${getTabColorClasses(tab, isActive)} whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-3 text-xs sm:text-sm font-medium transition-colors duration-200 flex-shrink-0`}
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <Icon className="h-8 w-8 sm:h-5 sm:w-5" />
                      <span className="hidden sm:inline">{tab.name}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {ActiveComponent ? <ActiveComponent /> : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Componente no encontrado para la pestaña: {activeTab}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Equipment;